import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLang } from "@/context/LanguageContext";

const SITE = "https://abnormalworld.it";

/* index.html ships default description/og tags; once a page renders <SEO>, drop them so crawlers see a single set. */
const STATIC_META = typeof document !== "undefined"
    ? Array.from(document.head.querySelectorAll('meta[name="description"], meta[property="og:description"], meta[name="twitter:description"], meta[property="og:title"], meta[name="twitter:title"], meta[property="og:image"], meta[name="twitter:image"]'))
    : [];
function useDropStaticDescription() {
    useEffect(() => {
        STATIC_META.forEach((m) => m.parentNode && m.remove());
    }, []);
}
const DEFAULT_OG = "https://images.pexels.com/photos/11049373/pexels-photo-11049373.jpeg?auto=compress&cs=tinysrgb&w=1200&h=630&fit=crop";

/**
 * Per-page SEO / OpenGraph block. Drop near the top of any page component:
 *
 *   <SEO title="Trail X" description="..." image="..." />
 *
 * It will:
 *  - set <title>
 *  - set meta description + canonical
 *  - set OG + Twitter Card tags (so WhatsApp / IG / FB preview is correct)
 *  - sync <html lang> with the active site language
 */
export default function SEO({
    title,
    description,
    image,
    path,            // e.g. "/trail/sentinel"
    type = "website",
    noindex = false,
}) {
    const { lang } = useLang();
    useDropStaticDescription();
    const fullTitle = title
        ? `${title} · Abnormalworld`
        : "Abnormalworld · MTB Enduro a Monte Argentario";
    const fullDesc = description
        || (lang === "en"
            ? "MTB Enduro on Monte Argentario. Trails with levels, video, live conditions, community rides, guides and shop."
            : "MTB Enduro a Monte Argentario. Trail con livelli, video, condizioni live, ride della community, guide e shop.");
    const fullImage = image || DEFAULT_OG;
    const url = `${SITE}${path || (typeof window !== "undefined" ? window.location.pathname : "")}`;
    const ogLocale = lang === "en" ? "en_GB" : "it_IT";
    const altLocale = lang === "en" ? "it_IT" : "en_GB";

    return (
        <Helmet>
            <html lang={lang === "en" ? "en" : "it"} />
            <title>{fullTitle}</title>
            <meta name="description" content={fullDesc} />
            <link rel="canonical" href={url} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}

            {/* OpenGraph */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={fullDesc} />
            <meta property="og:image" content={fullImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={fullTitle} />
            <meta property="og:site_name" content="Abnormalworld" />
            <meta property="og:locale" content={ogLocale} />
            <meta property="og:locale:alternate" content={altLocale} />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={fullDesc} />
            <meta name="twitter:image" content={fullImage} />
        </Helmet>
    );
}
